import { FC, lazy } from "react";

import { IModalProps, IModalResource } from "config/init/providers/ModalsProvider";
import { Environment, OR } from "config/types";

import AddAnotherBeneficiaryModal from "./AddAnotherBeneficiaryModal/AddAnotherBeneficiaryModal";
import AddWyshModal from "./AddWyshModal/AddWyshModal";
import ApplicationReceivedModal from "./ApplicationReceivedModal/ApplicationReceivedModal";
import BillingChargeFrequencyModal from "./BillingChargeFrequencyModal/BillingChargeFrequencyModal";
import CancelAddedWyshesModal from "./CancelAddedWyshesModal/CancelAddedWyshesModal";
import CancelPendingUpdatesModal from "./CancelPendingUpdatesModal/CancelPendingUpdatesModal";
import CancelPolicyModal from "./CancelPolicyModal/CancelPolicyModal";
import CantEditMonitoredModal from "./CantEditMonitoredModal/CantEditMonitoredModal";
import CloseAccountFailedModal from "./CloseAccountModals/CloseAccountFailedModal";
import CloseAccountModal from "./CloseAccountModals/CloseAccountModal";
import ConfirmBeneficiaryChangesModal from "./ConfirmBeneficiaryChangesModal/ConfirmBeneficiaryChangesModal";
import CustomerSupportModal from "./CustomerSupportModal/CustomerSupportModal";
import DocumentUploadErrorModal from "./DocumentUploadErrorModal/DocumentUploadErrorModal";
import DownloadPolicyPacketErrorModal from "./DownloadPolicyPacketErrorModal/DownloadPolicyPacketErrorModal";
import ExistingAccountModal from "./ExistingAccountModal/ExistingAccountModal";
import ExitFlowWarningModal from "./ExitFlowWarningModal/ExitFlowWarningModal";
import ExitRegistrationModal from "./ExitRegistrationModal/ExitRegistrationModal";
import ExitTriviaWarningModal from "./ExitTriviaWarningModal/ExitTriviaWarningModal";
import ExitWithoutSavingModal from "./ExitWithoutSavingModal/ExitWithoutSavingModal";
import GeneralErrorModal from "./GeneralErrorModal/GeneralErrorModal";
import IdentifiedExitModal from "./IdentifiedExitModal/IdentifiedExitModal";
import JourneyCompleted from "./JourneyCompleted/JourneyCompleted";
import JourneyExit from "./JourneyExit/JourneyExit";
import JourneyProgress from "./JourneyProgress/JourneyProgress";
import LoginAttemptWarningModal from "./LoginAttemptWarningModal/LoginAttemptWarningModal";
import ManualUWModal from "./ManualUWModal/ManualUWModal";
import NoPIUChangeErrorModal from "./NoPIUChangeErrorModal/NoPIUChangeErrorModal";
import NoWyshesAdded from "./NoWyshesAdded/NoWyshesAdded";
import PastDueBalanceModal from "./PastDueBalanceModal/PastDueBalanceModal";
import PaymentFailed from "./PaymentFailed/PaymentFailed";
import PayoutDetailsModal from "./PayoutDetailsModal/PayoutDetailsModal";
import PolicyBillingPaymentRetryFailureModal from "./PolicyBillingPaymentRetryFailureModal/PolicyBillingPaymentRetryFailureModal";
import PolicyBillingPaymentRetryProcessingModal from "./PolicyBillingPaymentRetryProcessingModal/PolicyBillingPaymentRetryProcessingModal";
import PolicyBillingPaymentRetrySuccessModal from "./PolicyBillingPaymentRetrySuccessModal/PolicyBillingPaymentRetrySuccessModal";
import PremiumCalculationModal from "./PremiumCalculationModal/PremiumCalculationModal";
import ReinstatementActivePolicyModal from "./ReinstatementActivePolicyModal/ReinstatementActivePolicyModal";
import ReinstatementErrorModal from "./ReinstatementErrorModal/ReinstatementErrorModal";
import RemoveBeneficiaryModal from "./RemoveBeneficiaryModal/RemoveBeneficiaryModal";
import RemoveLastBeneficiaryModal from "./RemoveLastBeneficiaryModal/RemoveLastBeneficiaryModal";
import RemoveWyshModal from "./RemoveWyshModal/RemoveWyshModal";
import ResendCodeModal from "./ResendCodeModal/ResendCodeModal";
import ResetPasswordModal from "./ResetPasswordModal/ResetPasswordModal";
import SaveChangesModal from "./SaveChangesModal/SaveChangesModal";
import SendBeneficiaryPacketModal from "./SendBeneficiaryPacketModal/SendBeneficiaryPacketModal";
import SettingsBillingPaymentRetryFailureModal from "./SettingsBillingPaymentRetryFailureModal/SettingsBillingPaymentRetryFailureModal";
import SettingsBillingPaymentRetryProcessingModal from "./SettingsBillingPaymentRetryProcessingModal/SettingsBillingPaymentRetryProcessingModal";
import SettingsBillingPaymentRetrySuccessModal from "./SettingsBillingPaymentRetrySuccessModal/SettingsBillingPaymentRetrySuccessModal";
import SettingsSuccess from "./SettingsSuccess/SettingsSuccess";
import SliderErrorMessageModal from "./SliderErrorMessageModal/SliderErrorMessageModal";
import Success from "./Success/Success";
import TestModal from "./TestModal/TestModal";
import UnderwritingDecisionModal from "./UnderwritingDecisionModal/UnderwritingDecisionModal";
import UnderwritingRejectionModal from "./UnderwritingRejectionModal/UnderwritingRejectionModal";
import UnderwritingRequiredModal from "./UnderwritingRequiredModal/UnderwritingRequiredModal";
import UnderwritingWaitTimeModal from "./UnderwritingWaitTimeModal/UnderwritingWaitTimeModal";
import UnmonitorWyshModal from "./UnmonitorWyshModal/UnmonitorWyshModal";
import UnsupportedStateModal from "./UnsupportedStateModal/UnsupportedStateModal";
import VerificationEmailSendModal from "./VerificationEmailSendModal/VerificationEmailSendModal";

type CollectionItem = OR<IModalResource, IModalResource["component"]>;
export type ModalKey = keyof typeof modals;

const noopModal: FC<IModalProps> = () => null;

let DevModals = {
  DevPlatformConstantModal: noopModal,
  DevPlatformEndpointModal: noopModal,
  DevResetDataModal: noopModal,
};

if (
  (process.env.REACT_APP_ENV as Environment) === "development" ||
  (process.env.REACT_APP_ENV as Environment) === "staging"
) {
  DevModals = {
    DevPlatformEndpointModal: lazy(
      () => import("./DevPlatformEndpointModal/DevPlatformEndpointModal")
    ),
    DevPlatformConstantModal: lazy(
      () => import("./DevPlatformConstantModal/DevPlatformConstantModal")
    ),
    DevResetDataModal: lazy(() => import("./DevResetDataModal/DevResetDataModal")),
  };
}

const modals = {
  AddWyshModal,
  AddAnotherBeneficiaryModal,
  ApplicationReceivedModal,
  BillingChargeFrequencyModal,
  CancelAddedWyshesModal,
  CancelPendingUpdatesModal,
  CancelPolicyModal,
  CantEditMonitoredModal,
  CloseAccountModal,
  CloseAccountFailedModal,
  ConfirmBeneficiaryChangesModal,
  CustomerSupportModal,
  ...DevModals,
  DocumentUploadErrorModal,
  DownloadPolicyPacketErrorModal,
  ExistingAccountModal,
  ExitFlowWarningModal,
  ExitRegistrationModal,
  ExitTriviaWarningModal,
  ExitWithoutSavingModal,
  GeneralErrorModal,
  IdentifiedExitModal,
  JourneyCompleted,
  JourneyExit,
  JourneyProgress,
  LoginAttemptWarningModal,
  ManualUWModal,
  NoPIUChangeErrorModal,
  NoWyshesAdded,
  PastDueBalanceModal,
  PaymentFailed,
  PayoutDetailsModal,
  PolicyBillingPaymentRetryFailureModal,
  PolicyBillingPaymentRetryProcessingModal,
  PolicyBillingPaymentRetrySuccessModal,
  PremiumCalculationModal,
  ReinstatementActivePolicyModal,
  ReinstatementErrorModal,
  RemoveBeneficiaryModal,
  RemoveLastBeneficiaryModal,
  RemoveWyshModal,
  ResendCodeModal,
  ResetPasswordModal,
  SaveChangesModal,
  SendBeneficiaryPacketModal,
  SettingsBillingPaymentRetryFailureModal,
  SettingsBillingPaymentRetryProcessingModal,
  SettingsBillingPaymentRetrySuccessModal,
  SettingsSuccess,
  SliderErrorMessageModal,
  Success,
  TestModal,
  VerificationEmailSendModal,
  UnderwritingDecisionModal,
  UnderwritingRejectionModal,
  UnderwritingRequiredModal,
  UnderwritingWaitTimeModal,
  UnmonitorWyshModal,
  UnsupportedStateModal,
} as const;

export default modals as Record<string, CollectionItem>;
